import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import { Box } from '@mui/system';
import type { FC } from 'react';

import { buildCountPhrase } from '@core/utils/strings';
import { LoadingSpinner } from '@web/components';

import { LocationToCopy } from './LocationToCopy';
import type { ProductPerPlant } from './useLocationProductsGrouping';

type ProductLocationSelectListProps = {
  plants: ProductPerPlant[];
  isLoading: boolean;
  selectedPlants: string[];
  onPlantSelectionChange: (newSelection: string[]) => void;
};

export const ProductLocationSelectList: FC<ProductLocationSelectListProps> = ({
  isLoading,
  plants,
  selectedPlants,
  onPlantSelectionChange,
}) => {
  const allSelected = !isLoading && plants.length === selectedPlants.length;
  const noSelected = selectedPlants.length === 0;
  const selectAllTitle = noSelected
    ? 'all locations'
    : `${buildCountPhrase(selectedPlants.length, '', 'location', 'locations')} selected`;
  const onSelectAll = (_, checked): void => {
    if (checked) {
      onPlantSelectionChange(plants.map((plant) => plant.plantId));
    } else {
      onPlantSelectionChange([]);
    }
  };
  const onSelectionChange = (changedPlantId, isSelected): void => {
    if (isSelected) {
      onPlantSelectionChange([...selectedPlants, changedPlantId]);
    } else {
      onPlantSelectionChange(selectedPlants.filter((plantId) => plantId !== changedPlantId));
    }
  };

  const items = plants
    .sort((a, b) => a.plantName.localeCompare(b.plantName))
    .map((plant) => {
      const isSelected = selectedPlants.includes(plant.plantId);

      return (
        <LocationToCopy
          key={plant.plantId}
          plant={plant}
          selected={isSelected}
          onSelectionChange={onSelectionChange}
        />
      );
    });

  const selectAllContent =
    plants.length > 0 ? (
      <FormControlLabel
        control={
          <Checkbox
            checked={allSelected}
            indeterminate={!allSelected && !noSelected}
            onChange={onSelectAll}
            disabled={isLoading}
          />
        }
        label={
          <Typography sx={{ textTransform: 'uppercase', color: '#727382' }}>
            {selectAllTitle}
          </Typography>
        }
      />
    ) : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {selectAllContent}
          {items}
        </>
      )}
    </Box>
  );
};
